import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Popup, type Anchor } from 'react-map-gl';
import { NavLink } from 'react-router-dom';
import { atom, useAtomValue, useStore } from 'jotai';
import { throttle } from 'lodash-es';
import { Button, Loading } from '@carbon/react';
import type { Map as MapboxMap, MapMouseEvent } from 'mapbox-gl';
import request from 'lib/request';
import type { SearchResult } from 'types/api-responses';
// Reuses the shared `.popup` class from components/map/map.module.scss for its
// styling, plus the well popover's generic scroll-list classes
// (`.wellPopupExpanded` / `.wellPopupItem`) so a multi-document dot lists like
// the wells popover does.
import mapStyles from 'components/map/map.module.scss';
import { format0 } from 'utils/number';
import { mapSearchRefAtom, allDatasetAtoms } from '../atoms';
import { withMapQueryReferrer } from '../document-link';
import { documentsConfig } from '../datasets/documents.config';
import { docLayerId, clusterCountLayerId } from './tile-layer-utils';
import { queryFeaturesNear } from './feature-hit-test';
import { buildCitation, humanize } from '../citation-format';
import type { DocumentRow } from '../types';

// Layer ids DynamicTileLayer renders for the documents dataset, rebuilt via the
// shared docLayerId helper so the id scheme lives in one place.
const DOC_LAYER_IDS = (documentsConfig.tileLayers ?? []).map((layer, idx) =>
  docLayerId(layer, idx)
);

// Invisible aggs hit-targets carrying `_count` — queried alongside the dots to
// tell how many documents stack on the hovered point.
const COUNT_LAYER_IDS = (documentsConfig.tileLayers ?? [])
  .map((layer, idx) => {
    return layer.clusterCount ? clusterCountLayerId(layer, idx) : null;
  })
  .filter((id): id is string => id !== null);

// Popover width for choosing which side to open toward — matches the rendered
// cap (`maxWidth` + `.docPopup` max-width) so the side choice can't clip.
const POPUP_WIDTH = 400;

// Pinned-stack list paging: one documents-search page per "Load more".
const PAGE_SIZE = 100;
// Cap the tile hit-test dedupe: a heavily-deeded centroid can carry hundreds of
// co-located hits, but this array only feeds the hover preview and the "is this
// a stack" check — the pinned list fetches the real, paged set separately.
const MAX_TILE_DOCS = 25;
// Half-size of the lng/lat box the pinned fetch scopes to — a hair wider than
// the hit-test pad so the stack under the click is caught, not just tile hits.
const FETCH_BOX_PAD = 6;
// Map-wide spatial contract keys stripped before re-scoping to the clicked box.
const SPATIAL_KEYS = [
  'bounding_box',
  'polygon_geojson',
  'bbox',
  'poly_geojson',
  'poly_wkt',
];
// Stable fallback for the documents payload read when the documents dataset
// isn't registered (then there are no document dots to click either).
const NO_SEARCH_ATOM = atom<{
  currentPayload?: Record<string, unknown>;
} | null>(null);

// The documents dataset's live search payload (its active filters), read
// without subscribing so the popup never triggers the dataset's own search.
const readDocPayload = (
  store: ReturnType<typeof useStore>
): Record<string, unknown> => {
  const atomToRead = (allDatasetAtoms.documents?.searchAtom ??
    NO_SEARCH_ATOM) as unknown as typeof NO_SEARCH_ATOM;
  return (
    store.get(atomToRead)?.currentPayload ??
    (documentsConfig.defaultPayload as Record<string, unknown>)
  );
};

interface BBox {
  sw_lng: number;
  sw_lat: number;
  ne_lng: number;
  ne_lat: number;
}

// A documents-search result row (fields inlined at the top level via
// render_path!), plus the two the shared DocumentRow type omits but the card
// needs: the STI type key and the provisioning/structuring status tokens.
type FetchedDocRow = DocumentRow & {
  documentTypeKey?: string;
  statuses?: string[];
};

// A small lng/lat box around the click (a hair wider than the hit-test) so the
// documents search returns the whole stack at this point, not just tile hits.
const bboxAround = (map: MapboxMap, point: { x: number; y: number }): BBox => {
  const sw = map.unproject([point.x - FETCH_BOX_PAD, point.y + FETCH_BOX_PAD]);
  const ne = map.unproject([point.x + FETCH_BOX_PAD, point.y - FETCH_BOX_PAD]);
  return { sw_lng: sw.lng, sw_lat: sw.lat, ne_lng: ne.lng, ne_lat: ne.lat };
};

// The dataset's active (non-spatial) filters re-scoped to the clicked box and
// paged — so the list matches the dots (same filters), just at this point.
const scopedPayload = (
  base: Record<string, unknown>,
  bbox: BBox,
  page: number
): Record<string, unknown> => {
  const rest: Record<string, unknown> = { ...base };
  for (const key of SPATIAL_KEYS) delete rest[key];
  return {
    ...rest,
    bounding_box: bbox,
    page,
    pageSize: PAGE_SIZE,
    response_config: { include_facets: false, include_bounding_box: false },
  };
};

// Map a search result row to the tile-shaped props DocumentCard reads, so a
// fetched stack renders identically to the tile hover card.
const resultToCardProps = (row: FetchedDocRow): Record<string, unknown> => {
  const statuses = row.statuses ?? [];
  const provisioning = statuses.includes('provisioned')
    ? 'provisioned'
    : statuses.includes('unprovisioned')
      ? 'not_provisioned'
      : undefined;
  return {
    type: row.documentTypeKey ?? '',
    id: row.id,
    volume: row.volume,
    page: row.page,
    instrument_number: row.instrumentNumber,
    document_subtype: row.documentSubtype,
    provisioning_status: provisioning,
    has_interpretation: (row.interpretationsCount ?? 0) > 0,
  };
};

// Choose the anchor and a max height from the space around the dot, so the
// popover opens into the screen (never off the edge) and its scroll list is
// capped to the space on that side — keeping the close button on-screen. Same
// idea as WellPopup's anchor logic.
const placeFor = (
  map: MapboxMap,
  longitude: number,
  latitude: number
): { anchor: Anchor; maxHeight: number } => {
  const { width, height } = map.getContainer().getBoundingClientRect();
  const p = map.project([longitude, latitude]);
  // Room for the popup tip and the close button's -10px overhang.
  const margin = 28;
  const spaceTop = p.y;
  const spaceBottom = height - p.y;
  const openUp = spaceBottom < spaceTop;
  const vertical = openUp ? 'bottom' : 'top';
  const horizontal = width - p.x < POPUP_WIDTH ? 'right' : 'left';
  const avail = (openUp ? spaceTop : spaceBottom) - margin;
  return {
    anchor: `${vertical}-${horizontal}` as Anchor,
    // Cap at the measured space (never floor above it) so a short map pane can't
    // push the footer / close button off-screen under the map's overflow:hidden.
    maxHeight: Math.min(avail, 480),
  };
};

const TYPE_LABELS: Record<string, string> = {
  courthouse: 'Courthouse',
  non_courthouse: 'Non-Courthouse',
  regulatory: 'Regulatory',
  afe: 'AFE',
  division_order: 'Division Order',
  royalty_check: 'Royalty Check',
};

// Tile `type` (Document STI demodulized/underscored) → its show path.
const TYPE_PATHS: Record<string, (id: string) => string> = {
  courthouse: (id) => `/courthouse_documents/${id}`,
  non_courthouse: (id) => `/non_courthouse_documents/${id}`,
  regulatory: (id) => `/regulatory_documents/${id}`,
  afe: (id) => `/afe_documents/${id}`,
  division_order: (id) => `/division_order_documents/${id}`,
  royalty_check: (id) => `/royalty_check_documents/${id}`,
};

interface DocStack {
  longitude: number;
  latitude: number;
  // Distinct documents rendered on this dot (deduped by id).
  docs: Array<Record<string, unknown>>;
  // geotile grid-cell count (the true total in this area); can exceed
  // docs.length when the tile's hits are capped, or 0 when the dot isn't
  // clustered. Drives the "+N in this area" note and whether a pin fetches.
  totalInArea: number;
  // The clicked point's lng/lat box, set on click so a pinned stack can fetch
  // the full list from the documents search (the tile only carries hits).
  bbox?: BBox;
}

// Read the documents under a point off the MVT tile properties (no extra fetch):
// every distinct centroid hit in the box, plus the grid-cell total. Null when
// the point misses every dot.
const readDocumentsAt = (
  map: MapboxMap,
  point: { x: number; y: number },
  lngLat: { lng: number; lat: number }
): DocStack | null => {
  const features = queryFeaturesNear(map, point, DOC_LAYER_IDS);
  if (!features.length) return null;

  // Co-located deeds can repeat across the box / adjacent tiles; dedupe by id.
  // Nameless features fall back to a positional key so two distinct id-less docs
  // are never collapsed into one.
  const seen = new Set<string>();
  const docs: Array<Record<string, unknown>> = [];
  for (const f of features) {
    const props = f.properties ?? {};
    const key = String(props.id ?? '') || `#${docs.length}`;
    if (seen.has(key)) continue;
    seen.add(key);
    docs.push(props);
    if (docs.length >= MAX_TILE_DOCS) break;
  }

  // Read the aggregation cell's `_count` at the same point to learn how many
  // documents stack on this dot; the aggs layer only exists for _count > 1.
  const aggs = queryFeaturesNear(map, point, COUNT_LAYER_IDS);
  const totalInArea = Number(aggs[0]?.properties?._count ?? 0);

  return { longitude: lngLat.lng, latitude: lngLat.lat, docs, totalInArea };
};

// "+N in this area" note for a single-document preview (hover / single pin),
// preferring the grid-cell total and falling back to the hits actually found.
const areaNote = ({ docs, totalInArea }: DocStack): ReactNode => {
  const extra =
    totalInArea > 1 ? totalInArea - 1 : Math.max(0, docs.length - 1);
  // "+N more" (not "N in this area") so it reads as the others besides the one
  // shown — the pinned list's header carries the "N in this area" total.
  return extra > 0 ? <> +{format0(extra)} more</> : null;
};

// One document's card body — its fields off the tile properties. `note` trails
// the header line for the single-document "+N in this area" hint.
const DocumentCard = ({
  props,
  note,
}: {
  props: Record<string, unknown>;
  note?: ReactNode;
}) => {
  const type = String(props.type ?? '');
  const id = String(props.id ?? '');
  const typeLabel = TYPE_LABELS[type] ?? (type ? humanize(type) : 'Document');
  const href = id ? TYPE_PATHS[type]?.(id) : undefined;
  // Vol/Pg · instrument citation, matching the Documents tab card header.
  const { citationFull } = buildCitation(
    props.volume,
    props.page,
    props.instrument_number
  );
  const subtype = props.document_subtype ? String(props.document_subtype) : '';
  const status = props.provisioning_status
    ? humanize(String(props.provisioning_status))
    : '';
  const hasInterp =
    props.has_interpretation === true ||
    props.has_interpretation === 'true' ||
    props.has_interpretation === 1;

  return (
    <div>
      <p>
        Document:{' '}
        {href ? (
          <NavLink
            to={withMapQueryReferrer(href)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {typeLabel} #{id}
          </NavLink>
        ) : (
          `${typeLabel} #${id}`
        )}
        {note}
      </p>
      {citationFull && <p>{citationFull}</p>}
      {subtype && <p>Subtype: {subtype}</p>}
      {status && <p>Status: {status}</p>}
      <p>Interpretation: {hasInterp ? 'Yes' : 'No'}</p>
    </div>
  );
};

// Every document at the clicked dot, in a scrollable list matching the wells
// popover. The tile only carries the hits rendered under the cursor, so this
// fetches the real list from the documents search (scoped to the click box,
// same filters as the map) and pages it via "Load more" — a stack can be
// hundreds deep. The header counts the true total from the response.
const DocumentStackList = ({
  bbox,
  maxHeight,
}: {
  bbox: BBox;
  maxHeight?: number;
}) => {
  const store = useStore();
  const [rows, setRows] = useState<FetchedDocRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const loadPage = useCallback(
    (next: number): AbortController => {
      setLoading(true);
      setFailed(false);
      if (next === 1) {
        setRows([]);
        setTotal(0);
      }
      const controller = new AbortController();
      request
        .post<SearchResult<FetchedDocRow>, unknown>(
          documentsConfig.searchEndpoint,
          scopedPayload(readDocPayload(store), bbox, next),
          controller
        )
        .then((res) => {
          if (res.type !== 'success') {
            setFailed(true);
            return;
          }
          setTotal(res.data.totalCount);
          setRows((prev) => {
            return next === 1
              ? res.data.results
              : [...prev, ...res.data.results];
          });
          setPage(next);
        })
        .catch(() => setFailed(true))
        .finally(() => setLoading(false));
      return controller;
    },
    [bbox, store]
  );

  // Fetch page 1 on open and whenever a new dot is clicked (bbox changes).
  useEffect(() => {
    const controller = loadPage(1);
    return () => controller.abort();
  }, [loadPage]);

  const shown = rows.length;
  const remaining = Math.max(0, total - shown);

  return (
    <div className={mapStyles.wellPopupExpanded} style={{ maxHeight }}>
      <p className={mapStyles.docStackHeader}>
        {format0(Math.max(total, shown))} documents in this area
      </p>
      {rows.map((row, i) => (
        <div
          key={`${String(row.id ?? '')}-${i}`}
          className={mapStyles.wellPopupItem}
        >
          <DocumentCard props={resultToCardProps(row)} />
        </div>
      ))}
      {loading && (
        <div className={mapStyles.docStackFooter}>
          <Loading small withOverlay={false} description="Loading documents" />
        </div>
      )}
      {failed && !loading && (
        <p className={mapStyles.docStackFooter}>
          Couldn’t load these documents.{' '}
          <Button kind="ghost" size="sm" onClick={() => loadPage(1)}>
            Retry
          </Button>
        </p>
      )}
      {!loading && !failed && remaining > 0 && (
        <div className={mapStyles.docStackFooter}>
          <Button kind="ghost" size="sm" onClick={() => loadPage(page + 1)}>
            Load {format0(Math.min(PAGE_SIZE, remaining))} more
          </Button>
        </div>
      )}
    </div>
  );
};

// Hover tooltip + click-to-pin popover for the documents map plots. A hover
// previews the top document; a click pins a popover (close button, links
// clickable) — for a dot stacking more than one document it lists them all in a
// scrollable popover, like the wells popover. The hover stays a single-document
// preview because a scrollable tooltip is unreachable: it clears on mouseout
// before the list could be scrolled or a link reached.
//
// `disabled` (measure / AOI-draw modes) stops opening new popovers so a
// point/vertex click doesn't also pin a document popover — same gate as
// WellClickPopup.
export const DocumentHoverPopup = ({
  disabled = false,
}: {
  disabled?: boolean;
}) => {
  const mapRef = useAtomValue(mapSearchRefAtom);
  const [hover, setHover] = useState<DocStack | null>(null);
  const [pinned, setPinned] = useState<DocStack | null>(null);

  // Read in the map handlers (whose closure only re-binds on mapRef) to skip the
  // hover hit-test while a popover is pinned — that render discards `hover`.
  const pinnedRef = useRef(false);
  pinnedRef.current = pinned !== null;

  useEffect(() => {
    if (!mapRef || disabled) return;
    const map = mapRef.getMap();

    // This miss-clear is the shared cursor owner: WellClickPopup sets `pointer`
    // on a well hover and never clears, relying on this. Don't stop clearing here.
    const clearHover = () => {
      map.getCanvas().style.cursor = '';
      setHover(null);
    };

    // Throttle the hit-test: mousemove fires 60+/sec, and each pass runs two box
    // queryRenderedFeatures calls plus a setHover that re-renders the Popup.
    const onMove = throttle((e: MapMouseEvent) => {
      // Skip the hit-test while pinned, but still clear: this handler is the
      // shared cursor owner, and a stale hover would flash back on close.
      if (pinnedRef.current) return clearHover();
      const stack = readDocumentsAt(map, e.point, e.lngLat);
      if (!stack) return clearHover();
      map.getCanvas().style.cursor = 'pointer';
      setHover(stack);
    }, 50);

    // Click a dot to pin it; click empty map to dismiss. Matches WellClickPopup:
    // a single click opens a persistent popover so its links can be clicked. The
    // click box is captured so a pinned stack can fetch its full document list.
    const onClick = (e: MapMouseEvent) => {
      const stack = readDocumentsAt(map, e.point, e.lngLat);
      if (stack) stack.bbox = bboxAround(map, e.point);
      setPinned(stack);
    };

    // A pan clears both so nothing floats at a stale location.
    const clearAll = () => {
      clearHover();
      setPinned(null);
    };

    map.on('mousemove', onMove);
    map.on('mouseout', clearHover);
    map.on('click', onClick);
    map.on('dragstart', clearAll);
    return () => {
      onMove.cancel();
      map.off('mousemove', onMove);
      map.off('mouseout', clearHover);
      map.off('click', onClick);
      map.off('dragstart', clearAll);
    };
  }, [mapRef, disabled]);

  // Pinned takes precedence so a single popover shows at a time; the hover
  // tooltip is suppressed while one is pinned (matching the wells pin flow).
  if (pinned) {
    // More than one document at the dot (either directly in the tile hits or per
    // the grid-cell count) → the fetched, paged list. Otherwise the single card.
    const isStack = pinned.docs.length > 1 || pinned.totalInArea > 1;
    const place = mapRef
      ? placeFor(mapRef.getMap(), pinned.longitude, pinned.latitude)
      : undefined;
    return (
      <Popup
        // Distinct key from the hover popup: react-map-gl only reads
        // `closeButton` when the mapbox popup mounts, so reusing the hover
        // instance (closeButton false) would drop the close button. Remounting
        // via the key gives the pinned popover its button — same fix as
        // WellPopup's `key={Number(closeButton)}`.
        key="doc-pinned"
        longitude={pinned.longitude}
        latitude={pinned.latitude}
        closeButton
        closeOnClick={false}
        className={`${mapStyles.popup} ${mapStyles.docPopup} ${
          isStack ? mapStyles.docPopupList : ''
        }`}
        maxWidth="400px"
        anchor={place?.anchor}
        onClose={() => setPinned(null)}
      >
        {isStack && pinned.bbox ? (
          <DocumentStackList bbox={pinned.bbox} maxHeight={place?.maxHeight} />
        ) : (
          <DocumentCard props={pinned.docs[0]} note={areaNote(pinned)} />
        )}
      </Popup>
    );
  }

  if (!hover) return null;

  const place = mapRef
    ? placeFor(mapRef.getMap(), hover.longitude, hover.latitude)
    : undefined;

  return (
    <Popup
      // Own key so the pinned popover (closeButton true) never reuses this
      // hover instance's mapbox popup — see the pinned popup above.
      key="doc-hover"
      longitude={hover.longitude}
      latitude={hover.latitude}
      closeButton={false}
      closeOnClick={false}
      className={`${mapStyles.popup} ${mapStyles.docPopup}`}
      maxWidth="400px"
      anchor={place?.anchor}
    >
      <DocumentCard props={hover.docs[0]} note={areaNote(hover)} />
    </Popup>
  );
};
